import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../App';
import api from '../utils/api';
import LoadingState from '../components/LoadingState';
import CategorySelector from '../components/CategorySelector';
import TagSelector from '../components/TagSelector';
import BrandSelector from '../components/BrandSelector';
import ImageUploader from '../components/ImageUploader';
import CustomFieldsManager from '../components/CustomFieldsManager';
import ProductSelector from '../components/ProductSelector';
import AttributeManager from '../components/AttributeManager';
import WordPressEditor from '../components/WordPressEditor';
import VariationManager from '../components/VariationManager';
import AIGenerateButton from '../components/AISettings/AIGenerateButton';
import useAIEnabled from '../hooks/useAIEnabled';

const ProductEditor = () => {
    const navigate = useNavigate();
    const { id: productId } = useParams();
    const { showSuccess, showError } = useToast();
    const [loading, setLoading] = useState(!!productId);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [productUrl, setProductUrl] = useState('');
    const [editUrl, setEditUrl] = useState('');

    const { aiEnabled } = useAIEnabled();

    const [formData, setFormData] = useState({
        // Basic Info
        name: '',
        slug: '',
        type: 'simple',
        status: 'publish',
        featured: false,
        catalog_visibility: 'visible',
        virtual: false,
        downloadable: false,
        
        // Descriptions
        short_description: '',
        description: '',
        
        // Pricing
        regular_price: '',
        sale_price: '',
        tax_status: 'taxable',
        tax_class: '',
        
        // Inventory
        sku: '',
        manage_stock: false,
        stock_quantity: '',
        stock_status: 'instock',
        backorders: 'no',
        sold_individually: false,
        
        // Shipping
        weight: '',
        length: '',
        width: '',
        height: '',
        shipping_class: '',
        
        // Linked Products
        upsell_ids: [],
        cross_sell_ids: [],
        
        // Attributes
        attributes: [],
        
        // Downloadable
        downloadable_files: [],
        download_limit: -1,
        download_expiry: -1,
        
        // Advanced
        purchase_note: '',
        menu_order: 0,
        enable_reviews: true,
        
        // Categories, Tags, Images, Brands
        categories: [],
        tags: [],
        brands: [],
        images: [],
        
        // Custom Fields
        custom_fields: []
    });

    useEffect(() => {
        if (productId) {
            loadProduct();
        }
    }, [productId]);

    const loadProduct = async () => {
        try {
            setLoading(true);
            const response = await api.getProduct(productId);
            
            if (response.success) {
                const product = response.product;
                
                // Set product URLs
                if (product.permalink) {
                    setProductUrl(product.permalink);
                }
                if (product.edit_url) {
                    setEditUrl(product.edit_url);
                }
                
                setFormData({
                    name: product.name || '',
                    slug: product.slug || '',
                    type: product.type || 'simple',
                    status: product.status || 'publish',
                    featured: product.featured || false,
                    catalog_visibility: product.catalog_visibility || 'visible',
                    virtual: product.virtual || false,
                    downloadable: product.downloadable || false,
                    short_description: product.short_description || '',
                    description: product.description || '',
                    regular_price: product.regular_price || '',
                    sale_price: product.sale_price || '',
                    tax_status: product.tax_status || 'taxable',
                    tax_class: product.tax_class || '',
                    sku: product.sku || '',
                    manage_stock: product.manage_stock || false,
                    stock_quantity: product.stock_quantity || '',
                    stock_status: product.stock_status || 'instock',
                    backorders: product.backorders || 'no',
                    sold_individually: product.sold_individually || false,
                    weight: product.weight || '',
                    length: product.dimensions?.length || '',
                    width: product.dimensions?.width || '',
                    height: product.dimensions?.height || '',
                    shipping_class: product.shipping_class || '',
                    upsell_ids: product.upsell_ids || [],
                    cross_sell_ids: product.cross_sell_ids || [],
                    attributes: Array.isArray(product.attributes) 
                        ? product.attributes 
                        : (product.attributes ? Object.values(product.attributes) : []),
                    downloadable_files: product.downloads || [],
                    download_limit: product.download_limit || -1,
                    download_expiry: product.download_expiry || -1,
                    purchase_note: product.purchase_note || '',
                    menu_order: product.menu_order || 0,
                    enable_reviews: product.reviews_allowed !== false,
                    categories: product.categories || [],
                    tags: product.tags || [],
                    brands: product.brands || [],
                    images: product.images || [],
                    custom_fields: product.custom_fields && Array.isArray(product.custom_fields) 
                        ? product.custom_fields.map((field, index) => ({
                            id: field.id || `${Date.now()}_${index}_${Math.random()}`,
                            key: field.key,
                            value: field.value || '',
                            type: field.type || 'text',
                            editable: field.editable || false
                        }))
                        : []
                });
            }
        } catch (err) {
            showError('Failed to load product: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            showError('Product name is required');
            return;
        }
        
        try {
            setSaving(true);
            
            // Prepare custom fields as meta_data
            const meta_data = formData.custom_fields.map(field => ({
                key: `_custom_${field.key}`,
                value: field.value
            }));

            const productData = {
                ...formData,
                // Filter out empty attributes
                attributes: formData.attributes.filter(attr => 
                    attr && attr.name && attr.options && attr.options.length > 0
                ),
                dimensions: {
                    length: formData.length || '',
                    width: formData.width || '',
                    height: formData.height || ''
                },
                meta_data: meta_data
            };

            if (productId) {
                const response = await api.updateProduct(productId, productData);
                if (response.success) {
                    showSuccess('Product updated successfully');
                    
                    // Update product URLs
                    if (response.product_url) {
                        setProductUrl(response.product_url);
                    }
                    if (response.edit_url) {
                        setEditUrl(response.edit_url);
                    }
                    
                    // Don't navigate away, stay on the page
                    // navigate('/products');
                } else {
                    throw new Error(response.message || 'Failed to update product');
                }
            } else {
                const response = await api.createProduct(productData);
                if (response.success) {
                    showSuccess('Product created successfully');
                    
                    // Update product URLs
                    if (response.product_url) {
                        setProductUrl(response.product_url);
                    }
                    if (response.edit_url) {
                        setEditUrl(response.edit_url);
                    }
                    
                    // Navigate to edit mode with the new product ID
                    if (response.product_id) {
                        navigate(`/products/edit/${response.product_id}`, { replace: true });
                    }
                } else {
                    throw new Error(response.message || 'Failed to create product');
                }
            }
        } catch (err) {
            showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleChange = async (field, value) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                [field]: value
            };
            
            // Handle product type change - reset virtual/downloadable if switching away from simple
            if (field === 'type' && value !== 'simple') {
                newData.virtual = false;
                newData.downloadable = false;
            }
            
            return newData;
        });
        
        // Handle product type change - switch to appropriate tab if current tab becomes invalid
        if (field === 'type') {
            // If switching away from variable and on variations tab, switch to general
            if (value !== 'variable' && activeTab === 'variations') {
                setActiveTab('general');
            }
            // If switching away from simple and on downloadable tab, switch to general
            if (value !== 'simple' && activeTab === 'downloadable') {
                setActiveTab('general');
            }
            // If switching away from simple and on shipping tab (and was virtual), switch to general
            if (value !== 'simple' && activeTab === 'shipping' && formData.virtual) {
                setActiveTab('general');
            }
            
            // Save product type immediately when changed (especially for variable products)
            if (productId) {
                try {
                    await api.updateProduct(productId, { type: value });
                    showSuccess('Product type updated');
                } catch (error) {
                    showError('Failed to update product type: ' + error.message);
                }
            }
        }
        
        // Handle virtual/downloadable changes
        if (field === 'virtual' && value === true && activeTab === 'shipping') {
            setActiveTab('general');
        }
        if (field === 'downloadable' && value === false && activeTab === 'downloadable') {
            setActiveTab('general');
        }
    };

    if (loading) {
        return <LoadingState message="Loading product..." fullScreen />;
    }

    return (
        <div className="page product-editor-page">
            <div className="page-container">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <button className="btn-back" onClick={() => navigate('/products')}>
                            ← Back to Products
                        </button>
                        <div>
                            <h1>{productId ? 'Edit Product' : 'Add New Product'}</h1>
                            <p>{productId ? 'Update product details' : 'Create a new product'}</p>
                        </div>
                    </div>
                    <div className="page-actions">
                        {productUrl && (
                            <a 
                                href={productUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary"
                                title="View product on your store"
                            >
                                👁️ View Product
                            </a>
                        )}
                        {editUrl && (
                            <a 
                                href={editUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary"
                                title="Edit in WooCommerce admin"
                            >
                                ⚙️ WC Admin
                            </a>
                        )}

                        {aiEnabled && (
                            <AIGenerateButton
                                type="product"
                                label="Generate with AI"
                                context={{
                                    type:              formData.type,
                                    virtual:           formData.virtual,
                                    downloadable:      formData.downloadable,
                                    // Current values — AI uses these to know what's already filled
                                    current: {
                                        name:              formData.name,
                                        regular_price:     formData.regular_price,
                                        sale_price:        formData.sale_price,
                                        sku:               formData.sku,
                                        stock_quantity:    formData.stock_quantity,
                                        manage_stock:      formData.manage_stock,
                                        weight:            formData.weight,
                                    },
                                }}
                                onResult={(data, text) => {
                                    if (data) {
                                        setFormData(prev => {
                                            const patch = {};

                                            // Text fields — only overwrite if AI returned them
                                            if (data.name              !== undefined) patch.name              = data.name;
                                            if (data.short_description !== undefined) patch.short_description = data.short_description;
                                            if (data.description       !== undefined) patch.description       = data.description;
                                            if (data.sku               !== undefined) patch.sku               = data.sku;

                                            // Pricing — applies to simple, external; skip for variable (set on variations)
                                            if (prev.type !== 'variable') {
                                                if (data.regular_price !== undefined && data.regular_price !== '')
                                                    patch.regular_price = String(data.regular_price);
                                                if (data.sale_price !== undefined && data.sale_price !== '' && data.sale_price !== null)
                                                    patch.sale_price = String(data.sale_price);
                                            }

                                            // Shipping weight
                                            if (data.weight !== undefined && data.weight !== '' && !prev.virtual)
                                                patch.weight = String(data.weight);

                                            // Stock — only if manage_stock or stock_quantity explicitly returned
                                            if (data.stock_quantity !== undefined && Number(data.stock_quantity) > 0) {
                                                patch.stock_quantity = String(data.stock_quantity);
                                                patch.manage_stock   = true;
                                                patch.stock_status   = 'instock';
                                            }
                                            if (data.manage_stock !== undefined && data.stock_quantity === undefined)
                                                patch.manage_stock = Boolean(data.manage_stock);

                                            // Virtual / Downloadable flags
                                            if (data.virtual      !== undefined) patch.virtual      = Boolean(data.virtual);
                                            if (data.downloadable !== undefined) patch.downloadable = Boolean(data.downloadable);

                                            return { ...prev, ...patch };
                                        });
                                        showSuccess('AI content applied! Review each field and save.');
                                    } else {
                                        // Raw text fallback → description
                                        setFormData(prev => ({ ...prev, description: text }));
                                        showSuccess('AI text applied to Description.');
                                    }
                                }}

                            />
                        )}
                        <button 
                            className="btn btn-secondary"
                            onClick={() => navigate('/products')}
                        >
                            Cancel
                        </button>
                        <button 
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : productId ? 'Update Product' : 'Create Product'}
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="product-editor-layout">
                        {/* Left Column - Main Content */}
                        <div className="product-editor-main">
                            {/* Product Name */}
                            <div className="editor-section">
                                <div className="section-content">
                                    <div className="form-group">
                                        <label>Product Name *</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => handleChange('name', e.target.value)}
                                            placeholder="Enter product name"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Short Description */}
                            <div className="editor-section">
                                <div className="section-header">
                                    <h3>Short Description</h3>
                                </div>
                                <div className="section-content">
                                    <WordPressEditor
                                        id="product-short-description"
                                        value={formData.short_description}
                                        onChange={(content) => handleChange('short_description', content)}
                                        placeholder="Enter a short description..."
                                        height={150}
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div className="editor-section">
                                <div className="section-header">
                                    <h3>Description</h3>
                                </div>
                                <div className="section-content">
                                    <WordPressEditor
                                        id="product-description"
                                        value={formData.description}
                                        onChange={(content) => handleChange('description', content)}
                                        placeholder="Enter product description..."
                                        height={300}
                                    />
                                </div>
                            </div>

                            {/* Product Data */}
                            <div className="editor-section product-data-section">
                                <div className="section-header">
                                    <h3>Product Data</h3>
                                    <select
                                        value={formData.type}
                                        onChange={(e) => handleChange('type', e.target.value)}
                                        className="product-type-select"
                                    >
                                        <option value="simple">Simple product</option>
                                        <option value="grouped">Grouped product</option>
                                        <option value="external">External/Affiliate product</option>
                                        <option value="variable">Variable product</option>
                                    </select>
                                </div>
                                
                                <div className="product-data-tabs">
                                    {/* Tab Navigation */}
                                    <div className="tab-nav">
                                        <button
                                            type="button"
                                            className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('general')}
                                        >
                                            General
                                        </button>
                                        <button
                                            type="button"
                                            className={`tab-btn ${activeTab === 'inventory' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('inventory')}
                                        >
                                            Inventory
                                        </button>
                                        {!formData.virtual && (
                                            <button
                                                type="button"
                                                className={`tab-btn ${activeTab === 'shipping' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('shipping')}
                                            >
                                                Shipping
                                            </button>
                                        )}
                                        {formData.downloadable && (
                                            <button
                                                type="button"
                                                className={`tab-btn ${activeTab === 'downloadable' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('downloadable')}
                                            >
                                                Downloadable
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className={`tab-btn ${activeTab === 'linked' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('linked')}
                                        >
                                            Linked Products
                                        </button>
                                        <button
                                            type="button"
                                            className={`tab-btn ${activeTab === 'attributes' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('attributes')}
                                        >
                                            Attributes
                                        </button>
                                        {formData.type === 'variable' && (
                                            <button
                                                type="button"
                                                className={`tab-btn ${activeTab === 'variations' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('variations')}
                                            >
                                                Variations
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className={`tab-btn ${activeTab === 'advanced' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('advanced')}
                                        >
                                            Advanced
                                        </button>
                                    </div>

                                    {/* Tab Content */}
                                    <div className="tab-content">

                                        {/* General Tab */}
                                        {activeTab === 'general' && (
                                            <div className="tab-panel">
                                                {/* Virtual and Downloadable - Only for Simple products */}
                                                {formData.type === 'simple' && (
                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label className="checkbox-label">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.virtual}
                                                                    onChange={(e) => handleChange('virtual', e.target.checked)}
                                                                />
                                                                <span>Virtual</span>
                                                            </label>
                                                            <p className="field-description">Virtual products don't require shipping</p>
                                                        </div>
                                                        <div className="form-group">
                                                            <label className="checkbox-label">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.downloadable}
                                                                    onChange={(e) => handleChange('downloadable', e.target.checked)}
                                                                />
                                                                <span>Downloadable</span>
                                                            </label>
                                                            <p className="field-description">Downloadable products give access to files</p>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Pricing - Only for Simple, External, and Grouped products */}
                                                {formData.type !== 'variable' && (
                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label>Regular Price ($)</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={formData.regular_price}
                                                                onChange={(e) => handleChange('regular_price', e.target.value)}
                                                                placeholder="0.00"
                                                            />
                                                        </div>
                                                        <div className="form-group">
                                                            <label>Sale Price ($)</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={formData.sale_price}
                                                                onChange={(e) => handleChange('sale_price', e.target.value)}
                                                                placeholder="0.00"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Variable Product Note */}
                                                {formData.type === 'variable' && (
                                                    <div className="info-box" style={{
                                                        padding: '12px 16px',
                                                        background: '#eff6ff',
                                                        border: '1px solid #3b82f6',
                                                        borderRadius: '6px',
                                                        marginBottom: '16px'
                                                    }}>
                                                        <p style={{ margin: 0, fontSize: '14px', color: '#1e40af' }}>
                                                            💡 <strong>Variable Product:</strong> Prices are set on individual variations, not the parent product. Go to the "Variations" tab to set prices.
                                                        </p>
                                                    </div>
                                                )}
                                                
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Tax Status</label>
                                                        <select
                                                            value={formData.tax_status}
                                                            onChange={(e) => handleChange('tax_status', e.target.value)}
                                                        >
                                                            <option value="taxable">Taxable</option>
                                                            <option value="shipping">Shipping only</option>
                                                            <option value="none">None</option>
                                                        </select>
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Tax Class</label>
                                                        <select
                                                            value={formData.tax_class}
                                                            onChange={(e) => handleChange('tax_class', e.target.value)}
                                                        >
                                                            <option value="">Standard</option>
                                                            <option value="reduced-rate">Reduced rate</option>
                                                            <option value="zero-rate">Zero rate</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Inventory Tab */}
                                        {activeTab === 'inventory' && (
                                            <div className="tab-panel">
                                                <div className="form-group">
                                                    <label>SKU</label>
                                                    <input
                                                        type="text"
                                                        value={formData.sku}
                                                        onChange={(e) => handleChange('sku', e.target.value)}
                                                        placeholder="Product SKU"
                                                    />
                                                </div>
                                                
                                                <div className="form-group">
                                                    <label className="checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.manage_stock}
                                                            onChange={(e) => handleChange('manage_stock', e.target.checked)}
                                                        />
                                                        <span>Manage stock?</span>
                                                    </label>
                                                </div>
                                                
                                                {formData.manage_stock && (
                                                    <div className="form-group">
                                                        <label>Stock Quantity</label>
                                                        <input
                                                            type="number"
                                                            value={formData.stock_quantity}
                                                            onChange={(e) => handleChange('stock_quantity', e.target.value)}
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                )}
                                                
                                                <div className="form-group">
                                                    <label>Stock Status</label>
                                                    <select
                                                        value={formData.stock_status}
                                                        onChange={(e) => handleChange('stock_status', e.target.value)}
                                                    >
                                                        <option value="instock">In stock</option>
                                                        <option value="outofstock">Out of stock</option>
                                                        <option value="onbackorder">On backorder</option>
                                                    </select>
                                                </div>
                                                
                                                {formData.manage_stock && (
                                                    <div className="form-group">
                                                        <label>Allow backorders?</label>
                                                        <select
                                                            value={formData.backorders}
                                                            onChange={(e) => handleChange('backorders', e.target.value)}
                                                        >
                                                            <option value="no">Do not allow</option>
                                                            <option value="notify">Allow, but notify customer</option>
                                                            <option value="yes">Allow</option>
                                                        </select>
                                                    </div>
                                                )}
                                                
                                                <div className="form-group">
                                                    <label className="checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.sold_individually}
                                                            onChange={(e) => handleChange('sold_individually', e.target.checked)}
                                                        />
                                                        <span>Sold individually</span>
                                                    </label>
                                                    <p className="field-description">Enable this to only allow one of this item to be bought in a single order</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Shipping Tab */}
                                        {activeTab === 'shipping' && (
                                            <div className="tab-panel">
                                                <div className="form-group">
                                                    <label>Weight (kg)</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={formData.weight}
                                                        onChange={(e) => handleChange('weight', e.target.value)}
                                                        placeholder="0.00"
                                                    />
                                                </div>
                                                
                                                <div className="form-group">
                                                    <label>Dimensions (cm)</label>
                                                    <div className="dimensions-group">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={formData.length}
                                                            onChange={(e) => handleChange('length', e.target.value)}
                                                            placeholder="Length"
                                                        />
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={formData.width}
                                                            onChange={(e) => handleChange('width', e.target.value)}
                                                            placeholder="Width"
                                                        />
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={formData.height}
                                                            onChange={(e) => handleChange('height', e.target.value)}
                                                            placeholder="Height"
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="form-group">
                                                    <label>Shipping Class</label>
                                                    <select
                                                        value={formData.shipping_class}
                                                        onChange={(e) => handleChange('shipping_class', e.target.value)}
                                                    >
                                                        <option value="">No shipping class</option>
                                                        <option value="heavy">Heavy Items</option>
                                                        <option value="light">Light Items</option>
                                                    </select>
                                                </div>
                                            </div>
                                        )}

                                        {/* Downloadable Tab */}
                                        {activeTab === 'downloadable' && formData.downloadable && (
                                            <div className="tab-panel">
                                                <div className="form-group">
                                                    <label>Downloadable Files</label>
                                                    {formData.downloadable_files.length === 0 ? (
                                                        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '12px' }}>
                                                            No files added yet
                                                        </p>
                                                    ) : (
                                                        <div className="downloadable-files-list">
                                                            {formData.downloadable_files.map((file, index) => (
                                                                <div key={index} className="downloadable-file-item">
                                                                    <div className="form-row">
                                                                        <div className="form-group">
                                                                            <label>File Name</label>
                                                                            <input
                                                                                type="text"
                                                                                value={file.name || ''}
                                                                                onChange={(e) => {
                                                                                    const newFiles = [...formData.downloadable_files];
                                                                                    newFiles[index].name = e.target.value;
                                                                                    handleChange('downloadable_files', newFiles);
                                                                                }}
                                                                                placeholder="File name"
                                                                            />
                                                                        </div>
                                                                        <div className="form-group" style={{ flex: 2 }}>
                                                                            <label>File URL</label>
                                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                                <input
                                                                                    type="url"
                                                                                    value={file.file || ''}
                                                                                    onChange={(e) => {
                                                                                        const newFiles = [...formData.downloadable_files];
                                                                                        newFiles[index].file = e.target.value;
                                                                                        handleChange('downloadable_files', newFiles);
                                                                                    }}
                                                                                    placeholder="https://example.com/file.zip"
                                                                                    style={{ flex: 1 }}
                                                                                />
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-secondary btn-sm"
                                                                                    onClick={() => {
                                                                                        // Open WordPress Media Library
                                                                                        if (window.wp && window.wp.media) {
                                                                                            const frame = window.wp.media({
                                                                                                title: 'Select File',
                                                                                                button: { text: 'Use this file' },
                                                                                                multiple: false
                                                                                            });

                                                                                            frame.on('select', function() {
                                                                                                const attachment = frame.state().get('selection').first().toJSON();
                                                                                                const newFiles = [...formData.downloadable_files];
                                                                                                newFiles[index] = {
                                                                                                    ...newFiles[index],
                                                                                                    name: newFiles[index].name || attachment.title || attachment.filename,
                                                                                                    file: attachment.url
                                                                                                };
                                                                                                handleChange('downloadable_files', newFiles);
                                                                                            });

                                                                                            frame.open();
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    Choose File
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-remove-file"
                                                                                onClick={() => {
                                                                                    const newFiles = formData.downloadable_files.filter((_, i) => i !== index);
                                                                                    handleChange('downloadable_files', newFiles);
                                                                                }}
                                                                                title="Remove file"
                                                                            >
                                                                                ✕
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={() => {
                                                            handleChange('downloadable_files', [
                                                                ...formData.downloadable_files,
                                                                { name: '', file: '', download_id: Date.now().toString() }
                                                            ]);
                                                        }}
                                                    >
                                                        + Add File
                                                    </button>
                                                </div>

                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Download Limit</label>
                                                        <input
                                                            type="number"
                                                            value={formData.download_limit}
                                                            onChange={(e) => handleChange('download_limit', e.target.value)}
                                                            placeholder="-1"
                                                        />
                                                        <p className="field-description">Leave blank or set to -1 for unlimited downloads</p>
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Download Expiry (days)</label>
                                                        <input
                                                            type="number"
                                                            value={formData.download_expiry}
                                                            onChange={(e) => handleChange('download_expiry', e.target.value)}
                                                            placeholder="-1"
                                                        />
                                                        <p className="field-description">Leave blank or set to -1 for never expires</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Linked Products Tab */}
                                        {activeTab === 'linked' && (
                                            <div className="tab-panel">
                                                <ProductSelector
                                                    selectedProducts={formData.upsell_ids.map(id => 
                                                        typeof id === 'object' ? id : { id, name: `Product #${id}` }
                                                    )}
                                                    onChange={(products) => handleChange('upsell_ids', products)}
                                                    label="Upsells"
                                                    description="Upsells are products which you recommend instead of the currently viewed product."
                                                    currentProductId={productId}
                                                />
                                                
                                                <div style={{ marginTop: '24px' }}>
                                                    <ProductSelector
                                                        selectedProducts={formData.cross_sell_ids.map(id => 
                                                            typeof id === 'object' ? id : { id, name: `Product #${id}` }
                                                        )}
                                                        onChange={(products) => handleChange('cross_sell_ids', products)}
                                                        label="Cross-sells"
                                                        description="Cross-sells are products which you promote in the cart, based on the current product."
                                                        currentProductId={productId}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Attributes Tab */}
                                        {activeTab === 'attributes' && (
                                            <div className="tab-panel">
                                                <AttributeManager
                                                    attributes={formData.attributes}
                                                    onChange={(attrs) => handleChange('attributes', attrs)}
                                                    productType={formData.type}
                                                    showError={showError}
                                                    showSuccess={showSuccess}
                                                />
                                            </div>
                                        )}

                                        {/* Variations Tab */}
                                        {activeTab === 'variations' && formData.type === 'variable' && (
                                            <div className="tab-panel">
                                                <div className="variations-header">
                                                    <h4>Product Variations</h4>
                                                    <p className="field-description">
                                                        Add variations for this variable product. First, save your attributes with "Used for variations" enabled.
                                                    </p>
                                                </div>

                                                {/* Check if we have variation attributes */}
                                                {formData.attributes && formData.attributes.some(attr => attr.variation) ? (
                                                    <VariationManager
                                                        productId={productId}
                                                        attributes={formData.attributes}
                                                        showError={showError}
                                                        showSuccess={showSuccess}
                                                    />
                                                ) : (
                                                    <div className="variations-empty">
                                                        <div className="empty-state">
                                                            <h4>No variation attributes found</h4>
                                                            <p>
                                                                To create variations, you need to:
                                                            </p>
                                                            <ol>
                                                                <li>Go to the <strong>Attributes</strong> tab</li>
                                                                <li>Add attributes (like Size, Color, etc.)</li>
                                                                <li>Enable "Used for variations" for each attribute</li>
                                                                <li>Click "Save Attributes"</li>
                                                                <li>Return to this tab to generate variations</li>
                                                            </ol>
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary"
                                                                onClick={() => setActiveTab('attributes')}
                                                            >
                                                                Go to Attributes
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Advanced Tab */}
                                        {activeTab === 'advanced' && (
                                            <div className="tab-panel">
                                                <div className="form-group">
                                                    <label>Purchase Note</label>
                                                    <textarea
                                                        value={formData.purchase_note}
                                                        onChange={(e) => handleChange('purchase_note', e.target.value)}
                                                        rows="3"
                                                        placeholder="Enter an optional note to send the customer after purchase"
                                                    />
                                                </div>
                                                
                                                <div className="form-group">
                                                    <label>Menu Order</label>
                                                    <input
                                                        type="number"
                                                        value={formData.menu_order}
                                                        onChange={(e) => handleChange('menu_order', e.target.value)}
                                                        placeholder="0"
                                                    />
                                                    <p className="field-description">Custom ordering position.</p>
                                                </div>
                                                
                                                <div className="form-group">
                                                    <label className="checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.enable_reviews}
                                                            onChange={(e) => handleChange('enable_reviews', e.target.checked)}
                                                        />
                                                        <span>Enable reviews</span>
                                                    </label>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Custom Fields */}
                            <div className="editor-section">
                                <div className="section-header">
                                    <h3>Custom Fields</h3>
                                </div>
                                <div className="section-content">
                                    <CustomFieldsManager
                                        customFields={formData.custom_fields}
                                        onChange={(fields) => handleChange('custom_fields', fields)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Sidebar */}
                        <div className="product-editor-sidebar">
                            {/* Product Status */}
                            <div className="sidebar-widget">
                                <div className="widget-header">
                                    <h4>Product Status</h4>
                                </div>
                                <div className="widget-content">
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => handleChange('status', e.target.value)}
                                        >
                                            <option value="publish">Published</option>
                                            <option value="draft">Draft</option>
                                            <option value="private">Private</option>
                                        </select>
                                    </div>
                                    
                                    <div className="form-group">
                                        <label>Visibility</label>
                                        <select
                                            value={formData.catalog_visibility}
                                            onChange={(e) => handleChange('catalog_visibility', e.target.value)}
                                        >
                                            <option value="visible">Shop and search results</option>
                                            <option value="catalog">Shop only</option>
                                            <option value="search">Search results only</option>
                                            <option value="hidden">Hidden</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Product Visibility */}
                            <div className="sidebar-widget">
                                <div className="widget-header">
                                    <h4>Product Visibility</h4>
                                </div>
                                <div className="widget-content">
                                    <div className="toggle-field">
                                        <label>
                                            <span>Featured Product</span>
                                            <div className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.featured}
                                                    onChange={(e) => handleChange('featured', e.target.checked)}
                                                />
                                                <span className="toggle-slider"></span>
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Featured Image */}
                            <div className="sidebar-widget">
                                <div className="widget-header">
                                    <h4>Featured Image</h4>
                                </div>
                                <div className="widget-content">
                                    <ImageUploader
                                        images={formData.images.length > 0 ? [formData.images[0]] : []}
                                        onChange={(images) => {
                                            if (images.length > 0) {
                                                // Replace featured image (first position)
                                                handleChange('images', [images[0], ...formData.images.slice(1)]);
                                            } else {
                                                // Remove featured image
                                                handleChange('images', formData.images.slice(1));
                                            }
                                        }}
                                        showError={showError}
                                        showSuccess={showSuccess}
                                        type="featured"
                                    />
                                </div>
                            </div>

                            {/* Product Gallery */}
                            <div className="sidebar-widget">
                                <div className="widget-header">
                                    <h4>Product Gallery</h4>
                                </div>
                                <div className="widget-content">
                                    <ImageUploader
                                        images={formData.images}
                                        onChange={(images) => handleChange('images', images)}
                                        showError={showError}
                                        showSuccess={showSuccess}
                                        type="gallery"
                                    />
                                </div>
                            </div>

                            {/* Product Categories */}
                            <div className="sidebar-widget">
                                <div className="widget-header">
                                    <h4>Product Categories</h4>
                                </div>
                                <div className="widget-content">
                                    <CategorySelector
                                        selectedCategories={formData.categories}
                                        onChange={(categories) => handleChange('categories', categories)}
                                        showError={showError}
                                        showSuccess={showSuccess}
                                    />
                                </div>
                            </div>

                            {/* Product Tags */}
                            <div className="sidebar-widget">
                                <div className="widget-header">
                                    <h4>Product Tags</h4>
                                </div>
                                <div className="widget-content">
                                    <TagSelector
                                        selectedTags={formData.tags}
                                        onChange={(tags) => handleChange('tags', tags)}
                                        showError={showError}
                                        showSuccess={showSuccess}
                                    />
                                </div>
                            </div>

                            {/* Product Brands */}
                            <div className="sidebar-widget">
                                <div className="widget-header">
                                    <h4>Product Brands</h4>
                                </div>
                                <div className="widget-content">
                                    <BrandSelector
                                        selectedBrands={formData.brands}
                                        onChange={(brands) => handleChange('brands', brands)}
                                        showError={showError}
                                        showSuccess={showSuccess}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductEditor;
